// Main menu — classic game-style overlay with a side rail of tabs and a
// content panel. Mirrors the editor pattern (body class toggles visibility,
// SIM keeps rendering behind a semi-transparent backdrop).
//
// Shown on first boot; ESC toggles. The tabs are the product's, handed in by
// its boot through initMenu(): src/menuTabs.play.ts for play and
// src/menuTabs.explore.ts for explore. Nothing here knows which tabs exist.

import { pushHudPositionsClearOf } from './hudDrag';
import { escapeHtml } from './html';
import { isMode, setMode as setAppMode } from './appMode';
import { analytics } from './visits/bus';
import { TEST_SWITCHES } from './urlFlags.ts';
import { isKey } from './keyBindings';

export type MenuSection = 'action' | 'bridge' | 'ops' | 'system';

export interface MenuTab {
  id: string;
  icon: string;
  label: string;
  section: MenuSection;
  /** True for tabs whose content is still a stub (renders a "Coming Soon" placeholder). */
  stub?: boolean;
  /** Re-render the pane on every open, for a pane that reads live state at render time. */
  rerenderOnOpen?: boolean;
  /** A count drawn on the rail row, re-read whenever `refreshOn` fires on the document. */
  badge?: { count: () => Promise<number>; refreshOn: string };
  /**
   * Render the tab's main-pane content into `host`, optionally returning a
   * teardown to run before the pane is cleared.
   */
  render: (host: HTMLElement) => void | (() => void);
}

const SECTION_HEADS: Record<MenuSection, string> = {
  action: 'Begin',
  bridge: 'Bridge',
  ops:    'Operations',
  system: 'System',
};

let TABS: readonly MenuTab[] = [];
let activeTabId = '';
/** Whether the pane on screen is one that draws into the sim's own canvas. */
let paneIsStage = false;
/** A stage pane given back on close has to be re-rendered on the next open. */
let paneNeedsRerender = false;
let elMenu: HTMLElement | null = null;
let elTabList: HTMLElement | null = null;
let elContent: HTMLElement | null = null;
let activePaneDispose: (() => void) | null = null;
let initialized = false;

// `?menuskiprow=<id>`: the rail leaves out one row its config holds, the
// control for tools/preview/menu-tabs-check.mjs's config leg.
const SKIP_ROW = TEST_SWITCHES ? new URLSearchParams(location.search).get('menuskiprow') : null;

// `?keeprail=1`: keeps the rail even with one tab, the control for
// tools/preview/menu-rail-collapse-check.mjs's explore leg. Dev only - a
// player never gets a way to bring the rail back.
const KEEP_RAIL = TEST_SWITCHES && new URLSearchParams(location.search).get('keeprail') === '1';

/** The tabs `initMenu` was given, in the order it was given them. */
export function menuTabs(): readonly MenuTab[] {
  return TABS;
}

export interface InitMenuOptions {
  /** False when nothing sits behind the menu to toggle back to (explore, for now): skips the Esc listener. */
  closable?: boolean;
}

/** The first tab is the one open at boot. */
export function initMenu(tabs: readonly MenuTab[], opts: InitMenuOptions = {}): void {
  if (initialized) return;
  initialized = true;
  TABS = tabs;
  activeTabId = tabs[0]?.id ?? '';
  elMenu = document.getElementById('main-menu');
  elTabList = document.getElementById('menu-tabs');
  elContent = document.getElementById('menu-content');
  if (!elMenu || !elTabList || !elContent) return;

  renderTabList();
  initNavCollapse();
  initStageChrome();
  // ?menutab=<id> deep-links a tab — used by headless verification shots.
  const urlTab = new URLSearchParams(location.search).get('menutab');
  setActiveTab(urlTab && TABS.some((t) => t.id === urlTab) ? urlTab : activeTabId);
  for (const tab of TABS) {
    const badge = tab.badge;
    if (!badge) continue;
    void refreshBadge(tab.id, badge.count);
    document.addEventListener(badge.refreshOn, () => void refreshBadge(tab.id, badge.count));
  }

  // ESC toggles menu (unless an input is focused — don't steal typing)
  if (opts.closable ?? true) {
    window.addEventListener('keydown', (e) => {
      if (!isKey(e, 'menu')) return;
      if (document.activeElement instanceof HTMLInputElement) return;
      if (document.activeElement instanceof HTMLTextAreaElement) return;
      if (isMode('editor')) return; // editor has its own back-button
      // The Atlas entered from the bridge (src/main.ts's land verb) has the
      // frame and its own Esc, which zooms out; the way back to the ship is that
      // wheel or its Return button, not the menu opening over the terrain.
      if (isMode('land')) return;
      e.preventDefault();
      toggleMenu();
    });
  }
}

export function showMenu(): void {
  setAppMode('menu', true);
  // setActiveTab is the only other place that decides this, and it does not
  // run on reopen when the active tab was already a stage tab before ESC.
  document.body.classList.toggle('menu-stage', paneIsStage);
  if (TABS.find((t) => t.id === activeTabId)?.rerenderOnOpen || paneNeedsRerender) {
    paneNeedsRerender = false;
    setActiveTab(activeTabId);
  }
}

export function hideMenu(): void {
  setAppMode('menu', false);
  // The Solar System pane is the world the ship flies in, re-hosted here:
  // closing the menu hands it back so cruise draws it around the ship again,
  // and the next open renders the tab afresh.
  if (paneIsStage) {
    activePaneDispose?.();
    activePaneDispose = null;
    if (elContent) elContent.innerHTML = '';
    paneIsStage = false;
    paneNeedsRerender = true;
  }
  // setActiveTab clears this when leaving a stage-mounted tab; closing the
  // menu without switching tabs first skipped that, leaving #app (and the
  // sim HUD inside it) permanently hidden behind a stale menu-stage class.
  document.body.classList.remove('menu-stage');
}

/**
 * Open the menu straight onto one tab. The pilot panel's own way into the
 * Solar System view, which otherwise costs an Esc and a pick off the rail.
 *
 * The tab is named before the pane is rendered, so a stage tab is mounted
 * once rather than mounted as whatever was open and immediately torn down.
 */
export function openMenuTab(id: string): void {
  if (!TABS.some((t) => t.id === id)) return;
  const showing = isMenuOpen() && activeTabId === id && !paneNeedsRerender;
  setAppMode('menu', true);
  if (showing) {
    document.body.classList.toggle('menu-stage', paneIsStage);
    return;
  }
  paneNeedsRerender = false;
  setActiveTab(id);
}

export function toggleMenu(): void {
  if (isMenuOpen()) hideMenu();
  else showMenu();
}

export function isMenuOpen(): boolean {
  return isMode('menu');
}

// ─────────────────────────────────────────────────────────────────────────────
// Navigation rail collapse
//
// The rail is 240px of the frame's width and every tab is one click away, so
// hiding it is normally a bad trade. The Atlas is the exception: it renders
// terrain into whatever it is given, and there the rail is 240px of Mars you
// cannot see. So the rail slides away and comes back, and the choice sticks.
// ─────────────────────────────────────────────────────────────────────────────

const NAV_KEY = 'menu.navCollapsed';

function setNavCollapsed(on: boolean): void {
  document.body.classList.toggle('nav-collapsed', on);
  const btn = document.getElementById('menu-nav-toggle');
  if (btn) {
    // The chevron points the way the rail will go, not the way it went.
    btn.innerHTML = on ? '\u203A' : '\u2039';
    btn.title = on ? 'Show navigation (\\)' : 'Hide navigation (\\)';
  }
  try {
    localStorage.setItem(NAV_KEY, on ? '1' : '0');
  } catch {
    /* private mode — the preference just will not outlive the session */
  }

  // Give the rail its space back.
  //
  // A dragged panel is position:fixed, so it sits above every pane and nothing
  // reflows it. Collapse the rail, drag a panel into the room that opened up,
  // expand again — and the rail slides back in UNDERNEATH it. Panels that
  // intrude get pushed just clear of the rail; anything already to its right is
  // left exactly where it was put.
  //
  // Measured after the slide finishes rather than assuming 240px, so the CSS
  // stays the single source of truth for how wide the rail is.
  if (!on) {
    const body = document.querySelector<HTMLElement>('.menu-body');
    let settled = false;
    const reclaim = (): void => {
      if (settled) return;
      settled = true;
      body?.removeEventListener('transitionend', onEnd);
      const rail = document.getElementById('menu-tabs');
      if (!rail) return;
      const r = rail.getBoundingClientRect();
      if (r.width > 1) pushHudPositionsClearOf(r.right);
    };
    // transitionend BUBBLES, and the rail fades in over 0.12s while the grid
    // takes 0.18s to open. Listening for any transition on the body therefore
    // fires two thirds of the way through the slide, and the rail measures
    // narrower than it is about to be — panels then get pushed only part of
    // the way clear. Wait for the property that actually moves the rail.
    const onEnd = (e: TransitionEvent): void => {
      if (e.target !== body || e.propertyName !== 'grid-template-columns') return;
      reclaim();
    };
    body?.addEventListener('transitionend', onEnd);
    // Fallback: transitionend never fires if the rail was already open, or if
    // the user has asked for reduced motion.
    window.setTimeout(reclaim, 260);
  }
}

function initNavCollapse(): void {
  let saved = false;
  try {
    saved = localStorage.getItem(NAV_KEY) === '1';
  } catch {
    saved = false;
  }
  setNavCollapsed(saved);

  document.getElementById('menu-nav-toggle')?.addEventListener('click', () => {
    if (document.body.classList.contains('menu-tabs-solo')) return;
    setNavCollapsed(!document.body.classList.contains('nav-collapsed'));
  });

  // Backslash toggles — next to Escape's job, and not a key any tab types.
  window.addEventListener('keydown', (e) => {
    if (e.key !== '\\' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (!isMenuOpen()) return;
    // With one tab there is no rail to bring back.
    if (document.body.classList.contains('menu-tabs-solo')) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (document.activeElement instanceof HTMLTextAreaElement) return;
    e.preventDefault();
    setNavCollapsed(!document.body.classList.contains('nav-collapsed'));
  });
}

/**
 * With one tab there is nothing to choose, so the rail disappears and the
 * pane takes its space (`body.menu-tabs-solo` in src/styles/menu.css). Read
 * off the same count `renderTabList` draws from, including `SKIP_ROW`, so a
 * config that adds or drops a tab is followed rather than special-cased per
 * product.
 */
function updateSoloTab(): void {
  const visible = TABS.filter((t) => t.id !== SKIP_ROW).length;
  document.body.classList.toggle('menu-tabs-solo', visible <= 1 && !KEEP_RAIL);
}

function renderTabList(): void {
  if (!elTabList) return;
  elTabList.innerHTML = '';
  updateSoloTab();

  const sections: MenuSection[] = ['action', 'bridge', 'ops', 'system'];
  for (const sec of sections) {
    // A product that lists no tab in a section gets no head for it.
    if (!TABS.some((t) => t.section === sec)) continue;
    const head = document.createElement('div');
    head.className = 'menu-section-head';
    head.textContent = SECTION_HEADS[sec];
    elTabList.appendChild(head);

    for (const tab of TABS) {
      if (tab.section !== sec) continue;
      if (tab.id === SKIP_ROW) continue;
      const row = document.createElement('button');
      row.className = 'menu-tab' + (tab.id === activeTabId ? ' active' : '');
      if (tab.stub) row.classList.add('stub');
      row.dataset['tabId'] = tab.id;
      row.innerHTML =
        `<span class="menu-tab-icon">${tab.icon}</span>` +
        `<span class="menu-tab-label">${escapeHtml(tab.label)}</span>` +
        (tab.stub ? '<span class="menu-tab-stub">TBA</span>' : '');
      row.addEventListener('click', () => setActiveTab(tab.id));
      elTabList.appendChild(row);
    }
  }
}

/** Badge a rail entry with its tab's count, or with nothing when the count is zero. */
async function refreshBadge(id: string, count: () => Promise<number>): Promise<void> {
  const row = document.querySelector<HTMLElement>(`.menu-tab[data-tab-id="${id}"]`);
  if (!row) return;
  row.querySelector('.menu-tab-badge')?.remove();
  const n = await count();
  if (n <= 0) return;
  const badge = document.createElement('span');
  badge.className = 'menu-tab-badge';
  badge.textContent = String(n);
  row.appendChild(badge);
}

function setActiveTab(id: string): void {
  if (!elContent) return;
  const tab = TABS.find((t) => t.id === id);
  if (!tab) return;
  activeTabId = id;
  analytics.menuTab(id);
  // Update tab highlight
  document.querySelectorAll<HTMLElement>('.menu-tab').forEach((el) => {
    el.classList.toggle('active', el.dataset['tabId'] === id);
  });
  // A pane that owns GPU resources hands back a teardown; the Atlas is the one
  // that does, and clearing the pane without it leaked a whole scene per switch.
  activePaneDispose?.();
  activePaneDispose = null;
  elContent.innerHTML = '';
  const teardown = tab.render(elContent);
  activePaneDispose = typeof teardown === 'function' ? teardown : null;
  // What this pane rendered, not whether a stage exists anywhere: the Atlas is
  // the world the ship flies in and its stage is mounted for the life of the
  // page, so `activeStage() !== null` is true even with no pane drawing.
  paneIsStage = elContent.querySelector('.atlas-root') !== null;
  document.body.classList.toggle('menu-stage', paneIsStage);
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage chrome
//
// A pane that mounts a stage (the Atlas) is drawing into the SIM canvas, which
// sits BEHIND this overlay: the pane has to become a hole in the chrome or the
// frame's own background covers what was just rendered. So while a stage is
// mounted the frame drops its fill and takes the dim as an outer shadow, which
// paints outside the border box only, and the backdrop stops blurring what it
// can no longer see. The SIM's HUD is inside #app too, and would otherwise
// show through the hole on top of the stage.
// ─────────────────────────────────────────────────────────────────────────────

function initStageChrome(): void {
  if (document.getElementById('menu-stage-css')) return;
  const style = document.createElement('style');
  style.id = 'menu-stage-css';
  style.textContent = `
    body.menu-stage .menu-backdrop {
      background: none;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    body.menu-stage .menu-frame {
      background-color: transparent;
      box-shadow:
        inset 0 0 0 1px var(--hud-border),
        0 0 60px rgba(80, 200, 255, 0.20),
        0 16px 60px rgba(0, 0, 0, 0.7),
        0 0 0 100vmax rgba(2, 4, 8, 0.82);
    }
    body.menu-stage .menu-header,
    body.menu-stage .menu-footer,
    body.menu-stage .menu-rail {
      background-color: rgba(6, 12, 22, 0.94);
    }
    body.menu-stage #app { visibility: hidden; }
    body.menu-stage #app canvas { visibility: visible; }
  `;
  document.head.appendChild(style);
}
